// /projects — index of all projects grouped by partner.
//
// Empty state IS the affordance: an empty /projects is the demo's
// expected starting point. The CTA invites pasting a brief; the
// "create manually" alt is one click away. No pre-seeded projects.
//
// When non-empty, projects render grouped by partner with per-row
// financial bar + status + target-date countdown.

import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { api, ApiError } from '../lib/api';
import {
  formatCADCompact,
  formatDate,
  formatCountdown,
  countdownToneCls,
  projectStatusMeta,
  type ProjectStatus,
  moneyToneCls,
} from '../lib/format';
import { PageFooter } from '../components/PageFooter';
import { isInsideEditable } from '../hooks/useGlobalShortcuts';

interface ProjectListRow {
  id: string;
  partner_profile_id: string;
  partner_name: string;
  partner_slug: string;
  name: string;
  description: string | null;
  ask_cad: number | null;
  match_available_cad: number | null;
  target_funding_date: number | null;
  status: ProjectStatus;
  application_count: number;
  match_count: number;
  committed_cad: number;
  created_at: number;
  updated_at: number;
}

interface Partner { id: string; name: string; slug: string }

const STATUS_FILTERS = [
  { value: 'active', label: 'Active (planning + seeking)' },
  { value: 'all', label: 'All statuses' },
  { value: 'planning', label: 'Planning only' },
  { value: 'seeking', label: 'Seeking only' },
  { value: 'funded', label: 'Funded' },
  { value: 'complete', label: 'Complete' },
  { value: 'cancelled', label: 'Archived' },
] as const;
type StatusFilter = (typeof STATUS_FILTERS)[number]['value'];

const SORT_OPTIONS = [
  { value: 'target_asc', label: 'Soonest target date' },
  { value: 'ask_desc', label: 'Largest ask' },
  { value: 'gap_desc', label: 'Largest gap' },
  { value: 'created_desc', label: 'Newest' },
] as const;
type SortValue = (typeof SORT_OPTIONS)[number]['value'];

export default function ProjectsPage() {
  const navigate = useNavigate();
  const [partnerFilter, setPartnerFilter] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('active');
  const [sort, setSort] = useState<SortValue>('target_asc');

  // Page-scoped shortcut: N → new project. Honors the "N" chip on the
  // primary CTA. Ignored when focus is inside an editable element.
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (isInsideEditable(e.target)) return;
      if (e.key === 'n' && !e.metaKey && !e.ctrlKey && !e.altKey) {
        e.preventDefault();
        navigate({ to: '/projects/new' });
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [navigate]);

  const partners = useQuery({
    queryKey: ['partners'],
    queryFn: () => api.get<Partner[]>('/partners'),
  });

  const projectsQ = useQuery({
    queryKey: ['projects', partnerFilter, statusFilter],
    queryFn: () => {
      const params = new URLSearchParams();
      if (partnerFilter !== 'all') params.set('partner_id', partnerFilter);
      if (statusFilter !== 'all' && statusFilter !== 'active') {
        params.set('status', statusFilter);
      }
      return api.get<ProjectListRow[]>(`/projects?${params.toString()}`);
    },
  });

  if (projectsQ.error instanceof ApiError && projectsQ.error.status === 401) {
    navigate({ to: '/welcome' });
    return null;
  }

  const allProjects = projectsQ.data?.data ?? [];

  const visible = useMemo(() => {
    let list = allProjects;
    if (statusFilter === 'active') {
      list = list.filter((p) => p.status === 'planning' || p.status === 'seeking');
    }
    return [...list].sort((a, b) => {
      switch (sort) {
        case 'target_asc':
          return (a.target_funding_date ?? Number.MAX_SAFE_INTEGER) -
                 (b.target_funding_date ?? Number.MAX_SAFE_INTEGER);
        case 'ask_desc':
          return (b.ask_cad ?? 0) - (a.ask_cad ?? 0);
        case 'gap_desc':
          return ((b.ask_cad ?? 0) - b.committed_cad) - ((a.ask_cad ?? 0) - a.committed_cad);
        case 'created_desc':
          return b.created_at - a.created_at;
      }
    });
  }, [allProjects, statusFilter, sort]);

  const grouped = useMemo(() => {
    const m = new Map<string, ProjectListRow[]>();
    for (const p of visible) {
      const arr = m.get(p.partner_profile_id) ?? [];
      arr.push(p);
      m.set(p.partner_profile_id, arr);
    }
    return [...m.entries()].sort((a, b) => {
      const aName = a[1][0]?.partner_name ?? '';
      const bName = b[1][0]?.partner_name ?? '';
      return aName.localeCompare(bName);
    });
  }, [visible]);

  const isEmpty = !projectsQ.isLoading && visible.length === 0;
  const noProjectsAtAll = !projectsQ.isLoading && allProjects.length === 0;
  const filterExcludesAll = !projectsQ.isLoading && allProjects.length > 0 && visible.length === 0;

  return (
    <div className="h-full flex flex-col min-h-0">
      <div className="flex-1 overflow-y-auto">
        <div className="max-w-[1200px] mx-auto px-5 py-5">
          {/* Header + primary CTA */}
          <header className="flex items-start justify-between gap-4 mb-4">
            <div>
              <h1 className="text-lg font-semibold text-neutral-900">Projects</h1>
              <p className="mt-1 text-xs text-neutral-600 max-w-2xl">
                Specific initiatives across the five DSSMSSAB partners. Pasting a brief from a program director is the fastest way to create one — Granted will extract details and find matching grants.
              </p>
            </div>
            <div className="flex flex-col items-end gap-1 shrink-0">
              <Link
                to="/projects/new"
                className="inline-flex items-center gap-2 bg-accent text-white rounded-md px-3 py-1.5 text-sm font-medium hover:bg-accent-hover"
              >
                + New project
                <kbd className="font-mono text-[10px] bg-black/20 px-1 py-0.5 rounded">N</kbd>
              </Link>
              <Link
                to="/projects/new"
                className="text-xs text-accent underline"
              >
                Or paste a brief →
              </Link>
            </div>
          </header>

          {/* Filters */}
          {!noProjectsAtAll && (
            <div className="flex flex-wrap gap-2 items-center mb-4 text-xs">
              <select
                aria-label="Partner filter"
                className="border border-neutral-300 rounded-md px-2 py-1"
                value={partnerFilter}
                onChange={(e) => setPartnerFilter(e.target.value)}
              >
                <option value="all">All partners</option>
                {(partners.data?.data ?? []).map((p) => (
                  <option key={p.id} value={p.id}>{p.name}</option>
                ))}
              </select>
              <select
                aria-label="Status filter"
                className="border border-neutral-300 rounded-md px-2 py-1"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value as StatusFilter)}
              >
                {STATUS_FILTERS.map((f) => <option key={f.value} value={f.value}>{f.label}</option>)}
              </select>
              <select
                aria-label="Sort"
                className="border border-neutral-300 rounded-md px-2 py-1 ml-auto"
                value={sort}
                onChange={(e) => setSort(e.target.value as SortValue)}
              >
                {SORT_OPTIONS.map((s) => (
                  <option key={s.value} value={s.value}>Sort: {s.label}</option>
                ))}
              </select>
            </div>
          )}

          {/* Loading */}
          {projectsQ.isLoading && (
            <p className="text-sm text-neutral-600">Loading projects — a moment.</p>
          )}

          {/* Hard error */}
          {projectsQ.error && !(projectsQ.error instanceof ApiError && projectsQ.error.status === 401) && (
            <div className="rounded-md border border-rose-200 bg-rose-50 p-3 text-sm text-rose-800">
              <p className="font-medium">Couldn't load projects.</p>
              <p className="mt-1 text-xs">Refresh the page. If the problem persists, contact your project administrator with the time and the URL.</p>
              <button onClick={() => projectsQ.refetch()} className="mt-2 text-xs underline hover:text-rose-900">Retry</button>
            </div>
          )}

          {/* True empty (no projects in system yet) — the demo's expected start */}
          {noProjectsAtAll && <EmptyState />}

          {/* Filter excludes everything */}
          {filterExcludesAll && (
            <div className="rounded-md border border-neutral-200 bg-neutral-50 p-4 text-sm">
              <p className="font-medium text-neutral-900">No projects match those filters.</p>
              <p className="mt-1 text-xs text-neutral-600">
                Try widening the partner or status filter, or{' '}
                <button
                  type="button"
                  onClick={() => { setPartnerFilter('all'); setStatusFilter('all'); }}
                  className="underline text-accent"
                >clear filters
                </button>.
              </p>
            </div>
          )}

          {/* Grouped list */}
          {!isEmpty && grouped.length > 0 && (
            <div className="space-y-6">
              {grouped.map(([partnerId, list]) => (
                <PartnerGroup key={partnerId} projects={list} />
              ))}
            </div>
          )}
        </div>
      </div>

      <PageFooter />
    </div>
  );
}

// ── Empty state ─────────────────────────────────────────────────────────

function EmptyState() {
  return (
    <div className="border border-neutral-200 rounded-md p-6 bg-neutral-50/40">
      <h2 className="text-base font-semibold text-neutral-900">No projects yet.</h2>
      <p className="mt-2 text-sm text-neutral-700 leading-relaxed max-w-xl">
        Projects are the specific initiatives partners pursue funding for — a paramedic-services accessibility retrofit, a childcare-expansion capital build, an Indigenous wellness program. The fastest way to start: paste a brief from a program director, and Granted will extract the project's name, scope, budget, and timeline before finding matching grants.
      </p>
      <div className="mt-4 flex flex-wrap items-center gap-3">
        <Link
          to="/projects/new"
          className="inline-flex items-center gap-2 bg-accent text-white rounded-md px-4 py-2 text-sm font-medium hover:bg-accent-hover"
        >
          Paste a project brief →
        </Link>
        <Link
          to="/projects/new"
          search={{ mode: 'manual' } as never}
          className="text-sm text-accent underline"
        >
          Or create one manually
        </Link>
      </div>
      <p className="mt-4 text-[11px] text-neutral-500">
        Tip: Granted only matches against grants a human has approved in <Link to="/admin/verify" className="underline">Verification</Link>. If matches look thin, there may be grants waiting to be verified.
      </p>
    </div>
  );
}

// ── Partner group ───────────────────────────────────────────────────────

function PartnerGroup({ projects }: { projects: ProjectListRow[] }) {
  const totalAsk = projects.reduce((acc, p) => acc + (p.ask_cad ?? 0), 0);
  const totalCommitted = projects.reduce((acc, p) => acc + p.committed_cad, 0);
  const totalGap = Math.max(0, totalAsk - totalCommitted);
  const partnerName = projects[0]?.partner_name ?? '';

  return (
    <section>
      <header className="flex items-baseline justify-between border-b border-neutral-200 pb-1.5 mb-3 gap-3">
        <h2 className="text-sm font-semibold text-neutral-900">
          {partnerName}
          <span className="ml-2 text-[11px] text-neutral-500 font-normal">{projects.length} project{projects.length === 1 ? '' : 's'}</span>
        </h2>
        <div className="text-[11px] text-neutral-600 tabular-nums flex gap-4">
          <span>Ask <span className="text-neutral-900 font-medium">{formatCADCompact(totalAsk)}</span></span>
          <span>Committed <span className="text-neutral-900 font-medium">{formatCADCompact(totalCommitted)}</span></span>
          <span>Gap <span className={moneyToneCls(totalGap, 'gap')}>{formatCADCompact(totalGap)}</span></span>
        </div>
      </header>
      <ul className="space-y-2">
        {projects.map((p) => <li key={p.id}><ProjectCard p={p} /></li>)}
      </ul>
    </section>
  );
}

// ── Project card ────────────────────────────────────────────────────────

function ProjectCard({ p }: { p: ProjectListRow }) {
  const ask = p.ask_cad ?? 0;
  const committed = p.committed_cad;
  const gap = Math.max(0, ask - committed);
  const pct = ask > 0 ? Math.min(100, Math.round((committed / ask) * 100)) : 0;
  const countdown = formatCountdown(p.target_funding_date);
  const status = projectStatusMeta[p.status];
  return (
    <Link
      to="/projects/$id"
      params={{ id: p.id }}
      className="block border border-neutral-200 rounded-md p-3 bg-white hover:border-neutral-300 hover:bg-neutral-50/50"
    >
      <div className="flex items-start gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="text-sm font-medium text-neutral-900 truncate">{p.name}</h3>
            <span className={'inline-block text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded ' + status.cls}>{status.label}</span>
            {countdown && (
              <span className={'inline-block text-[10px] px-1.5 py-0.5 rounded ' + countdownToneCls[countdown.tone]}>
                {p.target_funding_date && p.status !== 'cancelled' ? `Target ${formatDate(p.target_funding_date)} · ${countdown.text}` : countdown.text}
              </span>
            )}
          </div>
          {p.description && (
            <p className="mt-1 text-xs text-neutral-600 line-clamp-1">{p.description}</p>
          )}
        </div>
        <div className="shrink-0 text-right text-xs tabular-nums">
          <div className="text-neutral-700">Ask <span className="text-neutral-900 font-medium">{formatCADCompact(ask)}</span></div>
          <div className="text-neutral-700">Committed <span className="text-neutral-900 font-medium">{formatCADCompact(committed)}</span></div>
          <div className="text-neutral-700">Gap <span className={moneyToneCls(gap, 'gap')}>{formatCADCompact(gap)}</span></div>
        </div>
      </div>
      <div className="mt-2 flex items-center gap-3">
        <div
          role="progressbar"
          aria-label={`Funding progress: ${pct}% committed against the project ask`}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={pct}
          className="flex-1 h-1.5 bg-neutral-200 rounded overflow-hidden"
        >
          <div className={'h-full ' + (gap === 0 ? 'bg-emerald-600' : 'bg-accent')} style={{ width: `${pct}%` }} />
        </div>
        <div className="text-[11px] text-neutral-500 shrink-0">
          {p.application_count} app{p.application_count === 1 ? '' : 's'} · {p.match_count} match{p.match_count === 1 ? '' : 'es'}
        </div>
      </div>
    </Link>
  );
}
